import { type FC, useEffect, useMemo, useState } from "react"

import {
	getCourseIframeHostname,
	isCourseIframeApplicationOrigin,
	isCourseIframeDomainAllowed,
	normalizeCourseIframeUrl,
} from "@forento/core/utilities/courseIframe"
import CourseIframe from "@forento/ui/components/CourseIframe"

import Button from "~/components/Button"
import InputField, { InputTextArea } from "~/components/InputField"
import { useAlert } from "~/contexts/AlertContext"

import Attachments from "./Attachments"
import Links from "./Links"
import { ButtonsGroup, ButtonsRow } from "./styles"

type Props = {
	page: {
		id: number
		title: string
		iframeUrl: string | null
		attachments: { id: number; fileType: string; label: string; url: string }[]
		links: { id: number; label: string; url: string }[]
	}
	iframeAllowedDomains: string[]
	isSaving: boolean
	isDeleting: boolean
	onClose(): void
	onSavePage(data: { title: string; iframeUrl: string; allowIframeDomain?: boolean }): void
	onDeletePage(): void
}
const IframeContent: FC<Props> = ({
	page,
	iframeAllowedDomains,
	isSaving,
	isDeleting,
	onClose,
	onSavePage,
	onDeletePage,
}) => {
	const alert = useAlert()
	const [title, setTitle] = useState(page.title)
	const [embedInput, setEmbedInput] = useState(page.iframeUrl ?? "")
	const iframeUrl = useMemo(() => getIframeUrlFromInput(embedInput), [embedInput])

	useEffect(() => {
		setTitle(page.title)
		setEmbedInput(page.iframeUrl ?? "")
	}, [page])

	async function handleSave() {
		if (iframeUrl === null) {
			await alert.show(
				"Invalid embed source",
				"Paste a complete HTTPS URL or an iframe snippet with a safe HTTPS source.",
			)
			return
		}
		if (isCourseIframeApplicationOrigin(iframeUrl, [window.location.origin])) {
			await alert.show("Invalid embed source", "Forento cannot be embedded inside itself.")
			return
		}

		let allowIframeDomain = false
		if (!isCourseIframeDomainAllowed(iframeUrl, iframeAllowedDomains)) {
			const hostname = getCourseIframeHostname(iframeUrl)!
			const dialog = await alert.confirm(
				`Approve ${hostname}?`,
				"This domain is not on Forento's trusted provider list. Only approve it if you trust the source and expect students to interact with it.",
				{ confirmLabel: "Approve domain" },
			)
			if (!dialog.result) return
			dialog.close()
			allowIframeDomain = true
		}

		onSavePage({ title, iframeUrl, allowIframeDomain })
	}

	return (
		<>
			<InputField label="Title" value={title} onChange={setTitle} />
			<InputTextArea
				label="Embed URL or iframe code"
				value={embedInput}
				onChange={setEmbedInput}
				placeholder='<iframe src="https://example.com/embed"></iframe>'
			/>
			<p className="-mt-2 text-sm text-gray-500">
				Only the source URL is saved. Scripts and other attributes in pasted code are discarded.
			</p>
			{embedInput.trim() !== "" && (
				<div>
					<p className="mb-2 text-sm font-medium">Preview</p>
					<CourseIframe url={iframeUrl} title={title || "Embed preview"} compact />
				</div>
			)}
			<Attachments pageId={page.id} attachments={page.attachments} />
			<Links pageId={page.id} links={page.links} />
			<ButtonsRow>
				<ButtonsGroup>
					<Button variant="secondary" onClick={onClose} isDisabled={isSaving || isDeleting}>
						Cancel
					</Button>
					<Button
						variant="primary"
						isDisabled={title.trim().length === 0 || embedInput.trim().length === 0 || isDeleting}
						isLoading={isSaving}
						onClick={handleSave}
					>
						Save
					</Button>
				</ButtonsGroup>
				<Button variant="primary-danger" onClick={onDeletePage} isDisabled={isSaving} isLoading={isDeleting}>
					Delete
				</Button>
			</ButtonsRow>
		</>
	)
}

function getIframeUrlFromInput(input: string): string | null {
	const value = input.trim()
	if (value === "") return null

	if (value.startsWith("<")) {
		const document = new DOMParser().parseFromString(value, "text/html")
		const iframe = document.querySelector("iframe")
		if (iframe === null || document.querySelectorAll("iframe").length !== 1) return null
		return normalizeCourseIframeUrl(iframe.getAttribute("src") ?? "")
	}

	return normalizeCourseIframeUrl(value)
}

export default IframeContent
